import { MatrixBestPosition } from '../../api/assets';

// 矩阵识别中心的非组件公共件 (类型 / 常量 / 纯函数).
// 与 matrixParts.tsx 分开放: .tsx 里混导出非组件会破坏 Fast Refresh (eslint react-refresh/only-export-components).

/**
 * 最佳推荐一句话: 逐字段取最佳格, 缺了退回推荐参数 —— 新任务的 best_position 只有 row/col/score,
 * 功率速度只在 recommended_parameters 里; 旧存量反过来.
 */
export function matrixBestText(
    best: MatrixBestPosition | null | undefined,
    rec: { power?: number; speed?: number } | null | undefined,
): string {
    if (!best && !rec) return '—';
    return `功率 ${best?.power ?? rec?.power ?? '—'} · 速度 ${best?.speed ?? rec?.speed ?? '—'}`;
}

/** 单元格 (result.quality_evaluation.annotations). bbox 为像素. */
export interface MatrixCell {
    id?: number;
    row: number;
    col: number;
    bbox?: number[];
    type?: string; // best / good / acceptable / poor
    power?: number;
    speed?: number;
    score?: number;
    description?: string;
}

/** 参数档案 (result.quality_evaluation.parameter_profiles). bbox 为百分比. */
export interface MatrixProfile {
    key?: string;
    tag?: string;
    name?: string;
    row?: number;
    col?: number;
    bbox?: number[];
    power?: number;
    speed?: number;
    confidence?: number;
    description?: string;
    applicable_scenario?: string;
}

export const CELL_TYPE_STYLE: Record<string, { fill: string; stroke: string; text: string; bg: string; label: string }> = {
    best: { fill: 'rgba(16,185,129,.45)', stroke: '#059669', text: '#ffffff', bg: '#10b981', label: '最佳' },
    good: { fill: 'rgba(110,231,183,.40)', stroke: '#34d399', text: '#ffffff', bg: '#6ee7b7', label: '良好' },
    acceptable: { fill: 'rgba(252,211,77,.40)', stroke: '#fbbf24', text: '#78350f', bg: '#fcd34d', label: '可接受' },
    poor: { fill: 'rgba(252,165,165,.40)', stroke: '#f87171', text: '#7f1d1d', bg: '#fca5a5', label: '差' },
};
const CELL_DEFAULT = { fill: 'rgba(241,245,249,.30)', stroke: '#cbd5e1', text: '#475569', bg: '#e2e8f0', label: '—' };
export const cellStyle = (t?: string) => CELL_TYPE_STYLE[t || ''] || CELL_DEFAULT;

/**
 * 设备行的展示名: 平台机型名优先 (后端按 XCS 机型码对 device 表), 对不上回退机型码.
 * 两个码对到同一名字时 (如 JS002 → M2 与直接上报的 "M2") 名字后补码区分 ——
 * BarList / SuccessRateBars 以 label 作 key 与配色键, 重名会串.
 */
export function deviceLabels(rows: { device: string; name?: string }[]): Record<string, string> {
    const base = (r: { device: string; name?: string }) => r.name || r.device;
    const seen: Record<string, number> = {};
    rows.forEach((r) => { seen[base(r)] = (seen[base(r)] || 0) + 1; });
    const out: Record<string, string> = {};
    rows.forEach((r) => {
        const b = base(r);
        out[r.device] = seen[b] > 1 && r.name ? `${b}（${r.device}）` : b;
    });
    return out;
}
